/**
 * Flying message entity — an envelope that arcs between two agents.
 */

import type { Sprite } from "../sprites/SpriteSheet.js";
import { Entity } from "./Entity.js";
import { lerp, parabolicArc } from "../animation/Tweens.js";
import { depthKey } from "../engine/IsoMath.js";

export class MessageEntity extends Entity {
  private sprite: Sprite;
  private fromX: number;
  private fromY: number;
  private toX: number;
  private toY: number;
  private progress = 0;
  private duration: number;
  private arcHeight: number;

  /** When true, the message has reached its destination and should be removed. */
  completed = false;

  constructor(
    id: string,
    sprite: Sprite,
    from: { worldX: number; worldY: number },
    to: { worldX: number; worldY: number },
    options?: {
      duration?: number;
      arcHeight?: number;
    },
  ) {
    super(id, 0, 0, 3);
    this.sprite = sprite;
    this.fromX = from.worldX;
    this.fromY = from.worldY;
    this.toX = to.worldX;
    this.toY = to.worldY;
    this.duration = options?.duration ?? 1.2;
    this.arcHeight = options?.arcHeight ?? 25;

    // Start at source position
    this.worldX = this.fromX;
    this.worldY = this.fromY;
  }

  update(dt: number): void {
    this.progress += dt / this.duration;
    if (this.progress >= 1) {
      this.progress = 1;
      this.completed = true;
    }

    const t = this.progress;

    // Linear interpolation along path + parabolic arc for height
    this.worldX = lerp(this.fromX, this.toX, t);
    this.worldY = lerp(this.fromY, this.toY, t) + parabolicArc(t, this.arcHeight);

    // Depth: follow approximate midpoint for correct occlusion
    const midDepth = lerp(0, 999, t);
    this.depth = depthKey(midDepth, midDepth, 3);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const frame = this.sprite.frames[0];

    // Slight rotation based on arc tangent for natural look
    const scale = 1 + 0.2 * Math.sin(this.progress * Math.PI); // Grow slightly at peak
    ctx.save();
    ctx.translate(this.worldX, this.worldY);
    ctx.scale(scale, scale);
    ctx.drawImage(
      frame,
      -this.sprite.originX,
      -this.sprite.originY,
    );
    ctx.restore();
  }
}
